/**
 * Dynamic Field Visibility Module
 *
 * Handles showing/hiding form fields based on data attributes:
 * - data-controls-visibility="true" on trigger fields
 * - data-depends-on="fieldName" on dependent fields
 * - data-depends-on-value="value" on dependent fields
 */

interface DependentField {
  element: HTMLElement;
  dependsOn: string;
  dependsOnValue: string;
  requiresValidation?: boolean;
}

interface ControllerField {
  element: HTMLElement;
  input: HTMLInputElement;
  dependentFields: DependentField[];
}

class DynamicFieldVisibility {
  private controllerFields: Map<string, ControllerField> = new Map();
  private dependentFields: DependentField[] = [];

  public init(): void {
    try {
      this.findControllerFields();
      this.findDependentFields();
      this.linkDependencies();
      this.setupEventListeners();
      this.initializeVisibility();
    } catch (error) {
      console.error("Error initializing dynamic field visibility:", error);
    }
  }

  private findControllerFields(): void {
    const controllerElements = document.querySelectorAll(
      '[data-controls-visibility="true"]',
    );

    controllerElements.forEach((element) => {
      try {
        // Find the input within this form group
        const input = element.querySelector(
          'input[type="hidden"]',
        ) as HTMLInputElement;
        if (!input) {
          console.warn("Controller field missing hidden input:", element);
          return;
        }

        const fieldId = this.extractFieldId(input.id);
        if (!fieldId) {
          console.warn("Could not extract field ID from input:", input.id);
          return;
        }

        this.controllerFields.set(fieldId, {
          element: element as HTMLElement,
          input: input,
          dependentFields: [],
        });
      } catch (error) {
        console.error("Error processing controller field:", element, error);
      }
    });
  }

  private findDependentFields(): void {
    const dependentElements = document.querySelectorAll("[data-depends-on]");

    dependentElements.forEach((element) => {
      try {
        const dependsOn = element.getAttribute("data-depends-on");
        const dependsOnValue = element.getAttribute("data-depends-on-value");
        const requiresValidation =
          element.getAttribute("data-requires-validation") === "true";

        if (!dependsOn || !dependsOnValue) {
          console.warn("Dependent field missing required attributes:", element);
          return;
        }

        this.dependentFields.push({
          element: element as HTMLElement,
          dependsOn: dependsOn,
          dependsOnValue: dependsOnValue,
          requiresValidation: requiresValidation,
        });
      } catch (error) {
        console.error("Error processing dependent field:", element, error);
      }
    });
  }

  private linkDependencies(): void {
    this.dependentFields.forEach((dependentField) => {
      const controller = this.controllerFields.get(dependentField.dependsOn);
      if (controller) {
        controller.dependentFields.push(dependentField);
      } else {
        console.warn(
          `Controller field not found for dependency: ${dependentField.dependsOn}`,
        );
      }
    });
  }

  private setupEventListeners(): void {
    this.controllerFields.forEach((controller) => {
      // Listen for both change and input events
      ["change", "input"].forEach((eventType) => {
        controller.input.addEventListener(eventType, () => {
          this.updateFieldVisibility(controller);
        });
      });
    });
  }

  private initializeVisibility(): void {
    // Hide all dependent fields by default
    this.dependentFields.forEach((field) => {
      this.hideField(field.element);
    });

    // Then show fields based on current values
    this.controllerFields.forEach((controller) => {
      this.updateFieldVisibility(controller);
    });
  }

  private updateFieldVisibility(controller: ControllerField): void {
    const currentValue = controller.input.value;

    controller.dependentFields.forEach((dependentField) => {
      if (currentValue === dependentField.dependsOnValue) {
        this.showField(dependentField.element);
        if (dependentField.requiresValidation) {
          this.addRequiredValidation(dependentField.element);
        }
      } else {
        this.hideField(dependentField.element);
        this.clearFieldValue(dependentField.element);
        if (dependentField.requiresValidation) {
          this.removeRequiredValidation(dependentField.element);
        }

        // Notify that this field is being disabled for cleanup
        this.dispatchFieldDisabledEvent(dependentField.element);
      }
    });
  }

  private showField(element: HTMLElement): void {
    element.style.display = "";
    element.removeAttribute("aria-hidden");
  }

  private hideField(element: HTMLElement): void {
    element.style.display = "none";
    element.setAttribute("aria-hidden", "true");
  }

  private clearFieldValue(element: HTMLElement): void {
    try {
      // Find all inputs within the field and clear their values
      const inputs = element.querySelectorAll("input, select, textarea");
      inputs.forEach((input) => {
        if (
          input &&
          (input instanceof HTMLInputElement ||
            input instanceof HTMLSelectElement ||
            input instanceof HTMLTextAreaElement)
        ) {
          // Clear the value
          input.value = "";

          // Trigger change event to notify other systems
          const changeEvent = new Event("change", { bubbles: true });
          input.dispatchEvent(changeEvent);
        }
      });
    } catch (error) {
      console.error("Error clearing field value:", element, error);
    }
  }

  private extractFieldId(inputId: string): string | null {
    // Handle prefixed IDs like "ProjectCore.ProjectProgramAreaId"
    const parts = inputId.split(".");
    return parts[parts.length - 1] || null;
  }

  private addRequiredValidation(element: HTMLElement): void {
    try {
      // Check if this is a multiselect or typeahead field
      const isMultiselect =
        element.querySelector("[data-sync-multiselect]") ||
        element.querySelector("[data-async-multiselect]");
      const isTypeahead =
        element.querySelector("[data-valid-async-select]") ||
        element.querySelector("[data-sync-typeahead]");

      // For both multiselect and typeahead: use hidden input (it has the ID and stores the value)
      // For other fields: use text input
      let input: HTMLInputElement;
      if (isMultiselect || isTypeahead) {
        input = element.querySelector(
          'input[type="hidden"]',
        ) as HTMLInputElement;
      } else {
        input = element.querySelector(
          'input[type="text"], select, textarea',
        ) as HTMLInputElement;
      }

      if (!input) {
        console.warn("Could not find input for required validation:", element);
        return;
      }

      input.setAttribute("data-field-name", this.getFieldName(input));

      if (isMultiselect) {
        // For multiselect: set data-multiselect-required on hidden input
        input.setAttribute("data-multiselect-required", "true");
      } else if (isTypeahead) {
        // For typeahead: set required on the visible input (for form validation)
        const visibleInput = element.querySelector(
          'input[type="text"]',
        ) as HTMLInputElement;
        if (visibleInput) {
          visibleInput.setAttribute("required", "true");
          visibleInput.setAttribute(
            "data-field-name",
            this.getFieldName(input),
          );
        }
      } else {
        // For other fields: set required on the input
        input.setAttribute("required", "true");
      }

      // Trigger the existing validation system to pick up this field
      const validationSpan = document.querySelector(
        `[data-validation-for="${input.id}"]`,
      );
      if (validationSpan) {
        // Dispatch a custom event to notify the main validation system
        const event = new CustomEvent("dynamicFieldEnabled", {
          detail: { inputId: input.id, fieldName: this.getFieldName(input) },
        });
        document.dispatchEvent(event);
      }

      // Validate immediately using existing system
      const changeEvent = new Event("input", { bubbles: true });
      input.dispatchEvent(changeEvent);
    } catch (error) {
      console.error("Error adding required validation:", element, error);
    }
  }

  private removeRequiredValidation(element: HTMLElement): void {
    try {
      // Check if this is a multiselect or typeahead field
      const isMultiselect =
        element.querySelector("[data-sync-multiselect]") ||
        element.querySelector("[data-async-multiselect]");
      const isTypeahead =
        element.querySelector("[data-valid-async-select]") ||
        element.querySelector("[data-sync-typeahead]");

      // For both multiselect and typeahead: use hidden input to get the ID
      let input: HTMLInputElement;
      if (isMultiselect || isTypeahead) {
        input = element.querySelector(
          'input[type="hidden"]',
        ) as HTMLInputElement;
      } else {
        input = element.querySelector(
          'input[type="text"], select, textarea',
        ) as HTMLInputElement;
      }

      if (!input) {
        return;
      }

      if (isMultiselect) {
        // For multiselect: remove data-multiselect-required from hidden input
        input.removeAttribute("data-multiselect-required");
        input.removeAttribute("data-field-name");
      } else if (isTypeahead) {
        // For typeahead: remove required from visible input
        const visibleInput = element.querySelector(
          'input[type="text"]',
        ) as HTMLInputElement;
        if (visibleInput) {
          visibleInput.removeAttribute("required");
          visibleInput.removeAttribute("data-field-name");
        }
      } else {
        // For other fields: remove required from the input
        input.removeAttribute("required");
        input.removeAttribute("data-field-name");
      }

      // Clear any validation errors using the existing system
      const validationSpan = document.querySelector(
        `[data-validation-for="${input.id}"]`,
      );
      if (validationSpan) {
        validationSpan.innerHTML = "&nbsp;";
      }

      // For option-based fields (multiselect, typeahead), also clear the visible input validation
      const fieldGroup = input.closest("[data-option-field-group]");
      if (fieldGroup) {
        const visibleInput = fieldGroup.querySelector(
          "[data-option-field-visible]",
        ) as HTMLInputElement;
        if (visibleInput) {
          visibleInput.setCustomValidity("");
          visibleInput.removeAttribute("required");
        }
      }

      // Clean up typeahead validation ownership flags
      if (isTypeahead) {
        input.removeAttribute("data-typeahead-validated");
        if (validationSpan) {
          // Properly cleanup manager before nullifying reference
          const manager = (validationSpan as any)._manager;
          if (manager && typeof manager.cleanup === "function") {
            manager.cleanup();
          }
          (validationSpan as any)._isTypeaheadManaged = false;
          (validationSpan as any)._manager = null;
        }
      }
    } catch (error) {
      console.error("Error removing required validation:", element, error);
    }
  }

  private dispatchFieldDisabledEvent(element: HTMLElement): void {
    try {
      // Find the input field within the element
      const input = element.querySelector(
        'input[type="hidden"], input[type="text"], select, textarea',
      ) as HTMLInputElement;
      if (!input) {
        return;
      }

      // Dispatch a custom event to notify the main validation system
      const event = new CustomEvent("dynamicFieldDisabled", {
        detail: { inputId: input.id, fieldName: this.getFieldName(input) },
      });
      document.dispatchEvent(event);
    } catch (error) {
      console.error("Error dispatching field disabled event:", element, error);
    }
  }

  private getFieldName(input: HTMLInputElement): string {
    // Try to find the field name from the associated label or form group
    const formGroup = input.closest(".form-group");
    if (formGroup) {
      const label = formGroup.querySelector("label");
      if (label) {
        return label.textContent?.trim() || "Field";
      }

      // Look for the name in partial view data
      const nameElement = formGroup.querySelector("[data-field-name]");
      if (nameElement) {
        return nameElement.getAttribute("data-field-name") || "Field";
      }
    }

    return "Field";
  }
}

// Export the class and a function to initialize it
export { DynamicFieldVisibility };

export function initializeDynamicFieldVisibility(): void {
  const dynamicFields = new DynamicFieldVisibility();
  dynamicFields.init();
}
